import React, { useEffect, useState } from 'react';
import { Table, Spin, Typography, Button, Select, Row, Col, Popconfirm,message, Space, Input } from 'antd';
import { Link } from 'react-router-dom';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import PersistentDrawerLeft from "../../reusable/admin/Main";
import { restApi } from "../../../constants/api";
import styled from 'styled-components';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const Wrapper = styled.div` padding: 24px; `;
const StyledCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;
const StyledTitle = styled(Title)`
  && {
    margin: 0;
    font-weight: 600;
    font-size: 26px;
  }
`;
const AddButton = styled(Button)`
  background-color: #52c41a !important;
  border-color: #52c41a !important;
  border-radius: 8px;
`;
const SpinnerContainer = styled.div` text-align: center; padding: 3rem; `;
const TableWrapper = styled.div` 
overflow-x: auto;   

.ant-table-thead > tr > th {
  background-color: #77d6c9 !important;
}
.ant-table-thead > tr > th {
  color: black;
}`;

const EditButton = styled(Button)`
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  background-color: #5a7a76;
  border: none;
`;

const DeleteButton = styled(Button)`
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  background-color: #ff4d4f;
  border: none;
`;

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    role: '',
    user_type_id: '',
    active: '',
  });

  // Role Set 
  const rawRole = localStorage.getItem('role');
  const role = rawRole ? rawRole.trim().toUpperCase() : null;
  // console.log("Normalized Role:", role);

  // Fetch Designations only once
  useEffect(() => {
    fetchUserTypes();
  }, []);

  const fetchUserTypes = async () => {
    try {
      const response: any = await restApi('/api/user-types', 'GET');
      setUserTypes(response?.data || []);
    } catch (err) {
      console.error('Error fetching Designations', err);
    }
  };

  // Fetch users when filters change
  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams(filters).toString();
      const response: any = await restApi(`/api/users/list?${query}`, 'GET');
      setUsers(response?.data || []);
    } catch (err) {
      console.error('Error fetching users', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value: any, key: string) => {
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      [key]: value || '',
    }));
  };

  const searchQuery = searchText.trim().toLowerCase();
  const filteredUsers = users.filter((user: any) => !searchQuery || [
    user.id,
    `${user.prefixName || ''} ${user.name || ''}`,
    user.mobileNo,
    user.email,
    user.address,
    user.role,
    user.user_type,
    user.department_name,
    user.designation_code,
  ].some((value) => String(value ?? '').toLowerCase().includes(searchQuery)));

  const softDeleteUser = async (id: number) => {
    try {
      await restApi(`/api/users/${id}/soft_delete`, "POST", {}, true);
      // Refresh related lists in this tab and in other open tabs.
      window.dispatchEvent(new Event('users-updated'));
      try {
        localStorage.setItem('usersUpdatedAt', String(Date.now()));
      } catch {
        // Deletion still succeeds if browser storage is unavailable.
      }
      message.success("User deleted successfully");
      fetchUsers();
    } catch (err: any) {
      console.error("Soft delete error:", err);
      message.error(err?.response?.data?.message || err?.message || "Failed to delete user");
    }
  };
   
  const columns: any = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: 'Name', dataIndex: 'name', key: 'name',
      render: (text: string, record: any) => (
        <>
          {record.prefixName}. {record.name}
        </>
      )

    },
    { title: 'Address', dataIndex: 'address', key: 'address' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Mobile No', dataIndex: 'mobileNo', key: 'mobileNo' },
    { title: 'Role', dataIndex: 'role', key: 'role' },
    { title: 'Designation', dataIndex: 'user_type', key: 'user_type' },
    // { title: 'Adhar No', dataIndex: 'adharnumber', key: 'adharnumber' }, 
    // { title: 'PAN No', dataIndex: 'pannumber', key: 'pannumber' },
    // { title: 'Bank Name', dataIndex: 'bankName', key: 'bankName' },  
    // { title: 'Branch IFSC Code', dataIndex: 'branchIfscCode', key: 'branchIfscCode' },
    // { title: 'Account No', dataIndex: 'accountNumber', key: 'accountNumber' },
    { title: 'Code', dataIndex: 'designation_code', key: 'designation_code' },
    {
      title: 'DOJ',
      dataIndex: 'date_of_joining',
      key: 'date_of_joining',
      render: (text: any) => dayjs(text).format('DD-MMMM-YYYY'), // Format date
    },
    ...(role !== 'VIEWER'
      ? [
        {
          title: 'Actions',
          key: 'actions',
          align: 'center',
          render: (_: any, record: any) => (
            <Space>
              <Link to={`/admin/users/update/${record.id}`}>
                <EditButton type="primary" icon={<EditOutlined />} size="small">
                  Edit
                </EditButton>
              </Link>
              {role === "SUPERADMIN" && (
              <Popconfirm
                title="Delete user?"
                description="This will remove the user and their attendance cycles from lists and reports."
                okText="Yes"
                cancelText="No"
                onConfirm={() => softDeleteUser(record.id)}
              >
                <DeleteButton type="primary" icon={<DeleteOutlined />} size="small">
                  Delete
                </DeleteButton>
              </Popconfirm>
               )}
            </Space>
          ),
        },
      ]
      : [])
  ];

  return (
    <PersistentDrawerLeft>
      <Wrapper>
        <StyledCard>
          <Header>
            <StyledTitle level={3}>👤 Users</StyledTitle>
            {role !== 'VIEWER' && (
              <Link to="/admin/users/create">
                <AddButton icon={<PlusOutlined />}>Add User</AddButton>
              </Link>
            )}
          </Header>

          {/* Filters */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={16}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                aria-label="Search users"
                placeholder="Search by name, mobile, email, designation, department or code"
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
            {/* <Col xs={24} sm={8}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Filter by Role"
                onChange={(value) => handleFilterChange(value, 'role')}
              >
                <Option value="ADMIN">Admin</Option>
                <Option value="USER">User</Option>
                <Option value="SUPERADMIN">Super Admin</Option>
              </Select>
            </Col> */}
            <Col xs={24} sm={8}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Filter by Designation"
                onChange={(value) => handleFilterChange(value, 'user_type_id')}
              >
                {userTypes.map((ut: any) => (
                  <Option key={ut.id} value={ut.id}>
                    {ut.type_name}
                  </Option>
                ))}
              </Select>
            </Col>
            {/* <Col xs={24} sm={8}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Active Status"
                onChange={(value) => handleFilterChange(value, 'active')}
              >
                <Option value="true">Active</Option>
                <Option value="false">Inactive</Option>
              </Select>
            </Col> */}
          </Row>

          {/* Table */}
          {loading ? (
            <SpinnerContainer>
              <Spin size="large" />
            </SpinnerContainer>
          ) : (
            <TableWrapper>
              <Table
                columns={columns}
                dataSource={filteredUsers}
                rowKey="id"
                bordered
                pagination={{ pageSize: 10, current: currentPage, onChange: setCurrentPage }}
              />
            </TableWrapper>
          )}
        </StyledCard>
      </Wrapper>
    </PersistentDrawerLeft>
  );
};

export default UsersList;
